import type {
  AttributeKey, BuildingKey, DistrictKey, Formation, GeneKey, Resources, StandingOrders, TitleRank, Troops,
  SchemeType,
} from './types';
import { DAYS_PER_YEAR } from './time';

// ------------------------------------------------------------------ traits

export type TraitCategory = 'congenital' | 'personality' | 'acquired' | 'education';

export interface TraitDef {
  key: string;
  name: string;
  category: TraitCategory;
  description: string;
  /** Flat attribute modifiers. */
  attributes?: Partial<Record<AttributeKey, number>>;
  /** Added to health. */
  health?: number;
  /** Multiplies fertility. */
  fertility?: number;
  /** Personality opposites cannot coexist. */
  opposite?: string;
  /** Opinion bonus towards characters sharing the trait (personality only). */
  sameTraitOpinion?: number;
}

export const TRAIT_DEFS: Record<string, TraitDef> = {
  // congenital (expressed from genes)
  strong: { key: 'strong', name: 'Strong', category: 'congenital', description: 'Built like an ox.', attributes: { prowess: 4, martial: 1 }, health: 1 },
  genius: { key: 'genius', name: 'Genius', category: 'congenital', description: 'A rare, brilliant mind.', attributes: { diplomacy: 3, stewardship: 3, intrigue: 3, learning: 3, martial: 3 } },
  beautiful: { key: 'beautiful', name: 'Beautiful', category: 'congenital', description: 'Admired at every court.', attributes: { diplomacy: 2 }, fertility: 1.2 },
  frail: { key: 'frail', name: 'Frail', category: 'congenital', description: 'Weak of body.', attributes: { prowess: -3 }, health: -2 },
  infertile: { key: 'infertile', name: 'Infertile', category: 'congenital', description: 'Will likely never have children.', fertility: 0.05 },
  giant: { key: 'giant', name: 'Giant', category: 'congenital', description: 'Towering, but strained.', attributes: { prowess: 5 }, health: -1 },
  dwarf: { key: 'dwarf', name: 'Dwarf', category: 'congenital', description: 'Short of stature.', attributes: { prowess: -3, diplomacy: -1 } },
  clubfooted: { key: 'clubfooted', name: 'Clubfooted', category: 'congenital', description: 'Walks with a limp.', attributes: { prowess: -2, diplomacy: -1 } },

  // personality (3 per adult, chosen in childhood)
  brave: { key: 'brave', name: 'Brave', category: 'personality', description: 'Leads from the front.', attributes: { martial: 2, prowess: 2 }, opposite: 'craven', sameTraitOpinion: 10 },
  craven: { key: 'craven', name: 'Craven', category: 'personality', description: 'Avoids danger at all costs.', attributes: { martial: -2, intrigue: 2 }, opposite: 'brave', sameTraitOpinion: 5 },
  ambitious: { key: 'ambitious', name: 'Ambitious', category: 'personality', description: 'Wants more, always.', attributes: { diplomacy: 1, stewardship: 1, martial: 1, intrigue: 1 }, opposite: 'content', sameTraitOpinion: -10 },
  content: { key: 'content', name: 'Content', category: 'personality', description: 'Happy with their lot.', attributes: { intrigue: -1 }, opposite: 'ambitious', sameTraitOpinion: 10 },
  honest: { key: 'honest', name: 'Honest', category: 'personality', description: 'Says what they mean.', attributes: { diplomacy: 2, intrigue: -2 }, opposite: 'deceitful', sameTraitOpinion: 10 },
  deceitful: { key: 'deceitful', name: 'Deceitful', category: 'personality', description: 'Lies come easily.', attributes: { intrigue: 3, diplomacy: -1 }, opposite: 'honest', sameTraitOpinion: -5 },
  greedy: { key: 'greedy', name: 'Greedy', category: 'personality', description: 'Counts every coin.', attributes: { stewardship: 2, diplomacy: -1 }, opposite: 'generous', sameTraitOpinion: -5 },
  generous: { key: 'generous', name: 'Generous', category: 'personality', description: 'Open-handed with friends.', attributes: { diplomacy: 2, stewardship: -1 }, opposite: 'greedy', sameTraitOpinion: 10 },
  wrathful: { key: 'wrathful', name: 'Wrathful', category: 'personality', description: 'Quick to anger.', attributes: { martial: 2, diplomacy: -1, intrigue: -1 }, opposite: 'calm', sameTraitOpinion: -5 },
  calm: { key: 'calm', name: 'Calm', category: 'personality', description: 'Hard to provoke.', attributes: { diplomacy: 1, intrigue: 1 }, opposite: 'wrathful', sameTraitOpinion: 10 },
  just: { key: 'just', name: 'Just', category: 'personality', description: 'Follows the law.', attributes: { stewardship: 2, learning: 1, intrigue: -1 }, opposite: 'arbitrary', sameTraitOpinion: 10 },
  arbitrary: { key: 'arbitrary', name: 'Arbitrary', category: 'personality', description: 'The law is what they say.', attributes: { intrigue: 2, stewardship: -2 }, opposite: 'just', sameTraitOpinion: -5 },
  pious: { key: 'pious', name: 'Pious', category: 'personality', description: 'Devout and observant.', attributes: { learning: 2 }, opposite: 'cynical', sameTraitOpinion: 10 },
  cynical: { key: 'cynical', name: 'Cynical', category: 'personality', description: 'Believes in nothing much.', attributes: { intrigue: 2, learning: 1 }, opposite: 'pious', sameTraitOpinion: 0 },

  // education (one, from education focus, quality 1..3)
  edu_martial: { key: 'edu_martial', name: 'Trained Soldier', category: 'education', description: 'Educated in war.', attributes: { martial: 2, prowess: 1 } },
  edu_diplomacy: { key: 'edu_diplomacy', name: 'Courtier', category: 'education', description: 'Educated in the arts of court.', attributes: { diplomacy: 3 } },
  edu_stewardship: { key: 'edu_stewardship', name: 'Administrator', category: 'education', description: 'Educated in governance.', attributes: { stewardship: 3 } },
  edu_intrigue: { key: 'edu_intrigue', name: 'Schemer', category: 'education', description: 'Educated in secrets.', attributes: { intrigue: 3 } },
  edu_learning: { key: 'edu_learning', name: 'Scholar', category: 'education', description: 'Educated in letters and medicine.', attributes: { learning: 3 } },
  page: { key: 'page', name: 'Page', category: 'education', description: 'A child of the house, learning the basics of court and yard.' },
  squire: { key: 'squire', name: 'Squire', category: 'education', description: 'Serves in a liege’s retinue, training for the day of dubbing.' },

  // acquired
  wounded: { key: 'wounded', name: 'Wounded', category: 'acquired', description: 'Bears a fresh wound.', health: -2, attributes: { prowess: -2 } },
  maimed: { key: 'maimed', name: 'Maimed', category: 'acquired', description: 'Lost a limb in battle.', health: -1, attributes: { prowess: -6 } },
  scarred: { key: 'scarred', name: 'Scarred', category: 'acquired', description: 'Visibly marked by battle.', attributes: { prowess: 1 } },
  veteran: { key: 'veteran', name: 'Veteran', category: 'acquired', description: 'Has survived many battles.', attributes: { martial: 3 } },
  ill: { key: 'ill', name: 'Ill', category: 'acquired', description: 'Sick with fever.', health: -3 },
  drunkard: { key: 'drunkard', name: 'Drunkard', category: 'acquired', description: 'Rarely sober.', health: -1, attributes: { stewardship: -2 } },
  disgraced: { key: 'disgraced', name: 'Disgraced', category: 'acquired', description: 'Shamed before the realm.', attributes: { diplomacy: -3 } },
  knight: { key: 'knight', name: 'Knight', category: 'acquired', description: 'Sworn to serve a liege.', attributes: { prowess: 1 } },
};

export const PERSONALITY_TRAITS = Object.values(TRAIT_DEFS)
  .filter((t) => t.category === 'personality')
  .map((t) => t.key);

// ------------------------------------------------------------------ genes

export interface GeneDef {
  key: GeneKey;
  /** Trait key expressed. */
  trait: string;
  /** dominant: one allele expresses; recessive: both alleles needed. */
  mode: 'dominant' | 'recessive';
  /** Probability an allele is 1 in the founding population. */
  baseFrequency: number;
  /** Mutation chance per inherited allele (flip 0 -> 1). */
  mutation: number;
}

export const GENE_DEFS: GeneDef[] = [
  { key: 'strong', trait: 'strong', mode: 'recessive', baseFrequency: 0.15, mutation: 0.002 },
  { key: 'genius', trait: 'genius', mode: 'recessive', baseFrequency: 0.08, mutation: 0.001 },
  { key: 'beautiful', trait: 'beautiful', mode: 'recessive', baseFrequency: 0.15, mutation: 0.002 },
  { key: 'frail', trait: 'frail', mode: 'recessive', baseFrequency: 0.12, mutation: 0.004 },
  { key: 'infertile', trait: 'infertile', mode: 'recessive', baseFrequency: 0.06, mutation: 0.002 },
  { key: 'giant', trait: 'giant', mode: 'recessive', baseFrequency: 0.04, mutation: 0.001 },
  { key: 'dwarf', trait: 'dwarf', mode: 'recessive', baseFrequency: 0.04, mutation: 0.001 },
  { key: 'clubfooted', trait: 'clubfooted', mode: 'recessive', baseFrequency: 0.05, mutation: 0.003 },
];

/** Children of closely related parents: extra chance per negative gene to inherit the trait allele. */
export const INBREEDING_PENALTY = 0.25;

// ------------------------------------------------------------------ life

export const AGE_OF_MAJORITY = 16;
export const EDUCATION_START_AGE = 6;
/** Children become squires at this age (pages 6..11, squires 12+). */
export const SQUIRE_START_AGE = 12;
/**
 * Oldest age at which an untrained (no squireXp) landless character may still begin squire training. Older
 * untrained adults can never become knights; anyone already in training (squireXp < 100) may finish at any age.
 */
export const SQUIRE_MAX_START_AGE = 21;
/**
 * Nominal length of squire training in years. No longer a gate: a squire is dubbed when
 * `squireXp >= SQUIRE_XP_MAX` and of age (the monthly xp gain is tuned to reach it in ~4 years).
 */
export const DUBBING_MIN_SERVICE_YEARS = 4;
/** Squire training progress is 0..SQUIRE_XP_MAX; 100 = ready to be dubbed. */
export const SQUIRE_XP_MAX = 100;
/** xp at which a squire becomes 'seasoned' (+1 base prowess) / 'senior' (+1 base martial) / 'ready' (SQUIRE_XP_MAX). */
export const SQUIRE_XP_SEASONED = 33;
export const SQUIRE_XP_SENIOR = 66;
/** Training rank derived from squireXp. */
export type SquireRank = 'novice' | 'seasoned' | 'senior' | 'ready';
/** Knight tier derived from prestige (bachelor < 150 <= banneret < 400 <= champion). */
export type KnightTier = 'bachelor' | 'banneret' | 'champion';
export const KNIGHT_TIER_BANNERET = 150;
export const KNIGHT_TIER_CHAMPION = 400;
/** Personal squires one dubbed knight may keep, by the highest rank he holds ('landless' = a landless knight). */
export const SQUIRES_PER_KNIGHT: Record<TitleRank | 'landless', number> = {
  landless: 1, barony: 1, city: 1, county: 2, duchy: 2, kingdom: 3,
};
/** Size of the Guard of a landed title (cities keep none). */
export const GUARD_LIMIT: Partial<Record<TitleRank, number>> = { barony: 2, county: 3, duchy: 4, kingdom: 6 };
/** A murder scheme against a title holder loses this much success chance per guard squire of his primary title... */
export const GUARD_SCHEME_DEFENSE_PER_MEMBER = 0.02;
/** ...and this much more when the Guard has a captain. */
export const GUARD_CAPTAIN_DEFENSE = 0.04;
/** Guard squires train at this fraction of full speed when no knight of the holder's household can mentor them. */
export const GUARD_NO_MENTOR_SPEED = 0.5;
/** Landless AI houses seated in an awake kingdom (a kingdom with human houses) conceive at this fraction of the normal chance. */
export const AWAKE_LANDLESS_FERTILITY_MUL = 0.25;
/** Fill order: a kingdom whose human-house share is below this is a candidate target for new players (highest share first). */
export const FILL_TARGET_SHARE = 0.6;
/** A new house founded in the target kingdom receives this much gold... */
export const FILL_WELCOME_GOLD = 50;
/** ...and its liege (holder of the title its head serves as knight) gets this opinion of the new head (reason 'welcomed')... */
export const FILL_WELCOME_OPINION = 10;
/** ...for this many days (about three years). */
export const FILL_WELCOME_OPINION_DAYS = 3 * 360;
/** AI landed houses aim to keep ceil(KNIGHT_LIMIT of their head's primary title / this) household knights in service. */
export const HOUSEHOLD_KNIGHT_DIVISOR = 2;
/** A battle commander-less army is led by a sergeant with these Martial and Prowess (multiplier 1.14). */
export const SERGEANT_MARTIAL = 4;
export const SERGEANT_PROWESS = 4;
/** Knights riding with an army: each adds BASE + prowess x PER_PROWESS to battle strength, total capped at CAP. */
export const KNIGHT_BATTLE_BASE = 0.01;
export const KNIGHT_BATTLE_PER_PROWESS = 0.001;
export const KNIGHT_BATTLE_CAP = 0.25;
/** Per riding knight after each battle: killed / wounded chance, and (losing side only) captured chance. */
export const KNIGHT_BATTLE_KILLED = 0.02;
export const KNIGHT_BATTLE_WOUNDED = 0.06;
export const KNIGHT_BATTLE_CAPTURED = 0.04;
/** Prestige for each surviving knight on the winning side of a battle. */
export const KNIGHT_BATTLE_PRESTIGE = 5;
/** A lord's opinion penalty (and its length in years) for a knight whose house refused to ride with him through a war. */
export const KNIGHT_NO_RIDE_OPINION = -10;
export const KNIGHT_NO_RIDE_YEARS = 3;
export const MIN_FERTILE_AGE = 16;
export const FEMALE_FERTILE_MAX_AGE = 45;
export const MALE_FERTILE_MAX_AGE = 65;
export const PREGNANCY_DAYS = 270;
/** Base monthly chance of conception for a married fertile couple living together. */
export const BASE_MONTHLY_CONCEPTION = 0.06;
/** Life expectancy ~55 years: yearly health decay begins at this age. */
export const AGING_START_AGE = 40;

// ------------------------------------------------------------------ start state

export const START_STATE = {
  parentAge: 35,
  childCount: 3,
  childMinAge: 16,
  childMaxAge: 20,
  resources: { gold: 150, food: 100, timber: 20, stone: 0, iron: 5, horses: 2, wool: 10, manpower: 100 } as Resources,
  /** Monthly knight's fee paid by the liege. */
  knightFee: 3,
};

export const DEFAULT_STANDING_ORDERS: StandingOrders = {
  autoRaiseLevies: true,
  callToArms: 'join',
  retreatRatio: 1.5,
  marriageProposals: 'decline',
  rideWithLord: true,
};

// ------------------------------------------------------------------ inactivity

export interface InactivityThresholds {
  /** Real days of inactivity before the AI caretaker takes over. */
  caretakerDays: number;
  /** Real days before titles can be taken without tyranny. Null = never (low ranks). */
  deposableDays: number | null;
  /** Real days before game over (house becomes permanent AI). */
  gameOverDays: number;
}

/** Keyed by the highest rank the house head holds; 'landless' for knights/unlanded. */
export const INACTIVITY: Record<TitleRank | 'landless', InactivityThresholds> = {
  landless: { caretakerDays: 3, deposableDays: null, gameOverDays: 14 },
  barony: { caretakerDays: 3, deposableDays: null, gameOverDays: 14 },
  city: { caretakerDays: 3, deposableDays: null, gameOverDays: 14 },
  county: { caretakerDays: 3, deposableDays: 7, gameOverDays: 14 },
  duchy: { caretakerDays: 3, deposableDays: 7, gameOverDays: 14 },
  kingdom: { caretakerDays: 1, deposableDays: 3, gameOverDays: 14 },
};

/** Warning notifications are sent this many real days before each threshold. */
export const INACTIVITY_WARNING_DAYS = 1;

export function realDaysToDays(realDays: number): number {
  return realDays * DAYS_PER_YEAR;
}

/** Newcomer grace: a freshly founded player house is protected from murder/abduction schemes for 7 real days (= 7 game years). */
export const NEWCOMER_GRACE_DAYS = realDaysToDays(7);

// ------------------------------------------------------------------ the frontier (Design pass 2, step 6)

/** A frontier region opens once the world's human share has been at least this for FRONTIER_SHARE_DAYS ... */
export const FRONTIER_SHARE_THRESHOLD = 0.75;
export const FRONTIER_SHARE_DAYS = realDaysToDays(7);
/** ... and at least FRONTIER_MIN_JOINS new player houses were founded within the last FRONTIER_JOIN_WINDOW_DAYS. */
export const FRONTIER_MIN_JOINS = 20;
export const FRONTIER_JOIN_WINDOW_DAYS = realDaysToDays(7);
/** Lords of the old kingdoms may not declare war into a frontier region for this long after it opens (30 real days). */
export const FRONTIER_WAR_TRUCE_DAYS = realDaysToDays(30);
/** Shape of a frontier region: one kingdom's worth of land (the title tree of a worldgen kingdom: 3 duchies x 3 counties). */
export const FRONTIER_DUCHIES = 3;
export const FRONTIER_COUNTIES_PER_DUCHY = 3;
export const FRONTIER_SITES_PER_COUNTY = 2;
export const FRONTIER_CITY_SITES = 3;

export type SettleKind = 'site' | 'city_site';
export interface SettleCost { gold: number; timber: number; stone: number; manpower: number }
/** Cost of settling a site (a barony) or founding a city; paid up front, in full by the settler or half by a charter patron. */
export const SETTLE_COST: Record<SettleKind, SettleCost> = {
  site: { gold: 200, timber: 40, stone: 20, manpower: 100 },
  city_site: { gold: 400, timber: 80, stone: 40, manpower: 100 },
};
/** Settling takes one game year. */
export const SETTLE_DAYS = DAYS_PER_YEAR;
/** A new settlement cannot be attacked for 7 real days after it is founded. */
export const SETTLE_PROTECTION_DAYS = realDaysToDays(7);
/** A house may settle only one site until that settlement reaches this development. */
export const SETTLE_MIN_DEVELOPMENT = 2;
export const SETTLE_PROSPERITY = 50;
/** Charters: the patron pays this share of gold/timber/stone; the charter runs CHARTER_YEARS game years. */
export const CHARTER_PATRON_SHARE = 0.5;
export const CHARTER_YEARS = 5;
export const CHARTER_MAX_TAX = 0.05;
export const CHARTER_MAX_LEVY = 0.1;
/** An accepted charter proposal can be used by settle_site for this long. */
export const CHARTER_VALID_DAYS = 2 * DAYS_PER_YEAR;
/** AI patron acceptance score: opinion + clamp((gold left after paying - reserve) / 50, -10, 10) + house prestige / 100 - bias, accept if > 0. */
export const CHARTER_AI_GOLD_RESERVE = 300;
export const CHARTER_AI_BIAS = 5;
/** create_title: the requirement (settlements / counties / duchies held or led, in one frontier parent) and the price. */
export const CREATE_TITLE_COST = {
  county: { gold: 300, prestige: 50, needs: 2 },
  duchy: { gold: 600, prestige: 150, needs: 2 },
  kingdom: { gold: 1200, prestige: 400, needs: 2 },
} as const;

// ------------------------------------------------------------------ economy

export interface BuildingDef {
  key: BuildingKey;
  name: string;
  description: string;
  maxLevel: number;
  /** Cost per target level (multiplied by level). */
  cost: Partial<Resources>;
  /** Build time in game days per level. */
  days: number;
  allowedTerrain: 'any' | string[];
}

export const BUILDING_DEFS: Record<BuildingKey, BuildingDef> = {
  farmland: { key: 'farmland', name: 'Farmland', description: '+food each month.', maxLevel: 3, cost: { gold: 40, timber: 10 }, days: 360, allowedTerrain: ['plains', 'farmland', 'coast', 'hills'] },
  mine: { key: 'mine', name: 'Mine', description: '+iron each month.', maxLevel: 3, cost: { gold: 60, timber: 20 }, days: 540, allowedTerrain: ['hills', 'mountains'] },
  lumber_camp: { key: 'lumber_camp', name: 'Lumber Camp', description: '+timber each month.', maxLevel: 3, cost: { gold: 30 }, days: 360, allowedTerrain: ['forest', 'marsh', 'hills'] },
  quarry: { key: 'quarry', name: 'Quarry', description: '+stone each month.', maxLevel: 3, cost: { gold: 50, timber: 10 }, days: 540, allowedTerrain: ['hills', 'mountains', 'coast'] },
  stables: { key: 'stables', name: 'Stables', description: '+horses each month.', maxLevel: 3, cost: { gold: 50, timber: 20 }, days: 360, allowedTerrain: ['plains', 'farmland', 'hills'] },
  barracks: { key: 'barracks', name: 'Barracks', description: '+levy pool, faster regeneration.', maxLevel: 3, cost: { gold: 60, timber: 20, stone: 10 }, days: 540, allowedTerrain: 'any' },
  walls: { key: 'walls', name: 'Walls', description: '+garrison, slower sieges.', maxLevel: 3, cost: { gold: 80, stone: 40 }, days: 720, allowedTerrain: 'any' },
  market: { key: 'market', name: 'Market', description: '+gold each month.', maxLevel: 3, cost: { gold: 70, timber: 15 }, days: 540, allowedTerrain: 'any' },
};

// ------------------------------------------------------------------ holdings (Design pass 1, step 2)

/** Population bounds of a holding; the cap is POP_CAP_PER_DEVELOPMENT x development. */
export const POP_MIN = 50;
export const POP_CAP_PER_DEVELOPMENT = 500;
/** Monthly population growth = (prosperity - POP_GROWTH_NEUTRAL) / POP_GROWTH_DIVISOR (negative below neutral). */
export const POP_GROWTH_NEUTRAL = 40;
export const POP_GROWTH_DIVISOR = 1000;
/** Food a population eats per month: 1 per 100 people, taken from the holder house's food. */
export const FOOD_PER_100_POP = 1;
/** Share of the population lost per month while the holder house has no food. */
export const STARVATION_RATE = 0.02;

/** Prosperity (0..100) monthly terms. */
export const PROSPERITY_BASE_PEACE = 2;
/** The holder's council Steward adds stewardship / this. */
export const PROSPERITY_STEWARD_DIVISOR = 5;
/** Vassal tax rate above this fraction costs PROSPERITY_TAX_PER_POINT per percentage point. */
export const PROSPERITY_TAX_FREE_RATE = 0.2;
export const PROSPERITY_TAX_PER_POINT = 0.2;
export const PROSPERITY_AT_WAR = -5;
export const PROSPERITY_OCCUPIED = -15;
/** Market or Farmland at level 3. */
export const PROSPERITY_BUILDING_BONUS = 3;
/** Cities: +1 per caravan from the market (cap). */
export const PROSPERITY_CARAVAN_CAP = 3;
export const PROSPERITY_CATHEDRAL = 5;
export const PROSPERITY_HARBOUR = 1;
/** Cities: +1 prosperity per this many commerce earned last month, at most PROSPERITY_COMMERCE_CAP. */
export const PROSPERITY_COMMERCE_STEP = 20;
export const PROSPERITY_COMMERCE_CAP = 5;
/** A holding loses a development level after this many consecutive months below PROSPERITY_DECAY_BELOW. */
export const PROSPERITY_DECAY_BELOW = 20;
export const PROSPERITY_DECAY_MONTHS = 12;

/**
 * Gold per month = population / INCOME_POP_DIVISOR x (INCOME_DEV_BASE + development / 10)
 * x (prosperity / 100) x (1 + 0.25 x market level + steward bonus), times realm-size penalties.
 */
export const INCOME_POP_DIVISOR = 250;
export const INCOME_DEV_BASE = 0.5;
/** Levy pool target = population x this x (1 + barracks x 0.3). */
export const LEVY_POP_SHARE = 0.12;
/** Garrison target = (GARRISON_BASE + population x GARRISON_POP_SHARE + walls x 100) x (1 + marshal bonus). */
export const GARRISON_BASE = 25;
export const GARRISON_POP_SHARE = 0.012;

/** Population needed for a development project: this x (target - 1). */
export const DEV_POP_PER_LEVEL = 400;
export interface DevelopmentCost {
  gold: number;
  timber: number;
  stone: number;
  iron: number;
  /** Game years. */
  years: number;
}
/** Project costs by target level. Even levels are the design table; odd levels average their neighbours. */
const DEV_COST_EVEN: Record<number, DevelopmentCost> = {
  2: { gold: 60, timber: 20, stone: 0, iron: 0, years: 1 },
  4: { gold: 200, timber: 60, stone: 30, iron: 0, years: 2 },
  6: { gold: 450, timber: 120, stone: 80, iron: 0, years: 3 },
  8: { gold: 800, timber: 200, stone: 150, iron: 20, years: 4 },
  10: { gold: 1500, timber: 300, stone: 300, iron: 50, years: 5 },
};
export function developmentCost(target: number): DevelopmentCost {
  const t = Math.max(2, Math.min(10, Math.round(target)));
  const even = DEV_COST_EVEN[t];
  if (even) return { ...even };
  const lo = DEV_COST_EVEN[t - 1];
  const hi = DEV_COST_EVEN[t + 1];
  return {
    gold: Math.round((lo.gold + hi.gold) / 2),
    timber: Math.round((lo.timber + hi.timber) / 2),
    stone: Math.round((lo.stone + hi.stone) / 2),
    iron: Math.round((lo.iron + hi.iron) / 2),
    years: (lo.years + hi.years) / 2,
  };
}
/** Project length in whole months (time rounded to months). */
export function developmentMonths(target: number): number {
  return Math.max(1, Math.round(developmentCost(target).years * 12));
}
export function developmentPopNeeded(target: number): number {
  return DEV_POP_PER_LEVEL * (target - 1);
}
/** Building slots of a holding: 1 + floor(development / 2). A new building type needs a free slot. */
export function buildingSlots(development: number): number {
  return 1 + Math.floor(development / 2);
}
/** Population a fresh/legacy holding of this development starts with (capped at the population cap). */
export function defaultPopulation(development: number): number {
  const dev = Math.max(1, Math.floor(development));
  return Math.max(POP_MIN, Math.min(POP_CAP_PER_DEVELOPMENT * dev, DEV_POP_PER_LEVEL * (dev - 1) + 300));
}
export const DEFAULT_PROSPERITY = 60;

// ------------------------------------------------------------------ realm size (Design pass 1)

/** A ruler holds at most DEMESNE_BASE + floor(stewardship / DEMESNE_STEWARD_DIVISOR) holdings directly. */
export const DEMESNE_BASE = 3;
export const DEMESNE_STEWARD_DIVISOR = 5;
/** Per holding above the limit: income of all held holdings falls by this fraction, and direct vassals dislike the ruler by this much. */
export const DEMESNE_INCOME_PENALTY = 0.1;
export const DEMESNE_OPINION_PENALTY = 5;
/** Direct vassals above this number each cost -VASSAL_OPINION_PENALTY opinion with every direct vassal. */
export const VASSAL_LIMIT = 8;
export const VASSAL_OPINION_PENALTY = 2;
/** Holdings further than this many road-days from the ruler's capital produce DISTANCE_INCOME_PENALTY less. */
export const DISTANCE_LIMIT_DAYS = 10;
export const DISTANCE_INCOME_PENALTY = 0.2;

// ------------------------------------------------------------------ military

export interface TroopDef {
  key: keyof Troops;
  name: string;
  attack: number;
  defense: number;
  /** Monthly upkeep per 100 men. */
  upkeepGold: number;
  upkeepFood: number;
  /** Recruitment cost per 100 men. */
  recruit: Partial<Resources>;
  /** Days per road-day of travel (lower = faster). */
  speed: number;
}

export const TROOP_DEFS: Record<keyof Troops, TroopDef> = {
  levy: { key: 'levy', name: 'Levies', attack: 1, defense: 1, upkeepGold: 0.5, upkeepFood: 1, recruit: {}, speed: 1 },
  spearmen: { key: 'spearmen', name: 'Spearmen', attack: 2, defense: 4, upkeepGold: 2, upkeepFood: 1, recruit: { gold: 25, iron: 5, manpower: 100 }, speed: 1 },
  archers: { key: 'archers', name: 'Archers', attack: 3, defense: 1.5, upkeepGold: 2, upkeepFood: 1, recruit: { gold: 25, timber: 5, manpower: 100 }, speed: 1 },
  lightCavalry: { key: 'lightCavalry', name: 'Light Cavalry', attack: 3, defense: 2, upkeepGold: 3, upkeepFood: 2, recruit: { gold: 35, horses: 10, manpower: 100 }, speed: 0.6 },
  heavyCavalry: { key: 'heavyCavalry', name: 'Heavy Cavalry', attack: 6, defense: 4, upkeepGold: 5, upkeepFood: 2, recruit: { gold: 60, horses: 10, iron: 10, manpower: 100 }, speed: 0.8 },
};

/**
 * Formation matrix: attacker formation (row) vs defender formation (column).
 * Value multiplies the row side's damage. Rock-paper-scissors:
 * wedge beats line, line beats flanking, flanking beats shield_wall, shield_wall beats wedge.
 */
export const FORMATION_MATRIX: Record<Formation, Record<Formation, number>> = {
  line: { line: 1, wedge: 0.85, shield_wall: 1, flanking: 1.2 },
  wedge: { line: 1.2, wedge: 1, shield_wall: 0.8, flanking: 1 },
  shield_wall: { line: 1, wedge: 1.25, shield_wall: 1, flanking: 0.8 },
  flanking: { line: 0.85, wedge: 1, shield_wall: 1.25, flanking: 1 },
};

export const FORMATION_NAMES: Record<Formation, string> = {
  line: 'Line',
  wedge: 'Wedge',
  shield_wall: 'Shield Wall',
  flanking: 'Flanking',
};

/** Game days to raise levies (~1 real hour). */
export const LEVY_RAISE_DAYS = 14;
/** Truce length after a war, in game days (5 years). */
export const TRUCE_DAYS = 5 * DAYS_PER_YEAR;

/** Muster period: one game month between the declaration reaching the defender and the attacker's side being allowed into their land. */
export const MUSTER_DAYS = 30;

// ------------------------------------------------------------------ letters

/** Letter/proposal delivery: game days per road-day of distance (min 1). */
export const LETTER_DAYS_PER_ROAD_DAY = 0.5;
export const PROPOSAL_EXPIRY_DAYS = 90;

// ------------------------------------------------------------------ world size (MVP)

export type KnightHouseholdsPerRank = { [R in Exclude<TitleRank, 'city'>]: number };

export interface MvpWorldConfig {
  /** Kingdoms generated at world creation (Phase 4). */
  kingdoms: number;
  /** Per kingdom. */
  duchies: number;
  countiesPerDuchy: number;
  baroniesPerCounty: number;
  cities: number;
  presimYears: number;
  /**
   * AI knight households seeded at world creation, per rank of the title they serve (GDD: "Knights
   * can serve any liege from baron upward"; "Baron hosts and pays knights"). Knights of a title live
   * at that title's seat. City mayors host no knights.
   */
  knightHouseholdsPerRank: KnightHouseholdsPerRank & { city?: number };
  targetHumanHouses: number;
}

export const MVP_WORLD: MvpWorldConfig = {
  /** Kingdoms generated at world creation (Phase 4). */
  kingdoms: 6,
  /** Per kingdom. */
  duchies: 3,
  countiesPerDuchy: 3,
  baroniesPerCounty: 2,
  cities: 3,
  presimYears: 150,
  /** Knight households per rank at world creation; knights of a title live at its seat. */
  knightHouseholdsPerRank: { kingdom: 1, duchy: 1, county: 1, barony: 1 },
  targetHumanHouses: 200,
};

/** Reference world size the per-kingdom scalings are tuned for (the default world). */
export const DEFAULT_KINGDOMS = MVP_WORLD.kingdoms;
/** Houses (all kinds, AI + human) a kingdom is sized for when the world is sized by `expectedHouses`. */
export const HOUSES_PER_KINGDOM = 60;
export const MIN_KINGDOMS = 2;
/** Map area per kingdom: the default 6 kingdoms sit on a 3 x 2 grid of 1000 x 700. */
export const MAP_CELL_W = 1000 / 3;
export const MAP_CELL_H = 700 / 2;
export const DEFAULT_MAP_WIDTH = 1000;
export const DEFAULT_MAP_HEIGHT = 700;

/** Kingdoms for a world expecting `expectedHouses` houses: ceil(houses / HOUSES_PER_KINGDOM), at least MIN_KINGDOMS. */
export function kingdomsForHouses(expectedHouses: number): number {
  return Math.max(MIN_KINGDOMS, Math.ceil(expectedHouses / HOUSES_PER_KINGDOM));
}

/** Grid that fits `kingdoms` kingdoms and the map size it needs (6 kingdoms -> 3 x 2 -> 1000 x 700). */
export function mapLayout(kingdoms: number): { cols: number; rows: number; width: number; height: number } {
  const cols = Math.ceil(Math.sqrt(kingdoms * 1.5));
  const rows = Math.ceil(kingdoms / cols);
  return { cols, rows, width: Math.round(MAP_CELL_W * cols), height: Math.round(MAP_CELL_H * rows) };
}

export const RANK_NAMES: Record<TitleRank, { title: string; male: string; female: string }> = {
  kingdom: { title: 'Kingdom', male: 'King', female: 'Queen' },
  duchy: { title: 'Duchy', male: 'Duke', female: 'Duchess' },
  county: { title: 'County', male: 'Count', female: 'Countess' },
  barony: { title: 'Barony', male: 'Baron', female: 'Baroness' },
  city: { title: 'City', male: 'Lord Mayor', female: 'Lady Mayor' },
};

export const COUNCIL_SEAT_DEFS = {
  chancellor: { name: 'Chancellor', attribute: 'diplomacy', description: 'Improves vassal opinion and proposal acceptance.' },
  steward: { name: 'Steward', attribute: 'stewardship', description: 'Raises tax income and building speed.' },
  marshal: { name: 'Marshal', attribute: 'martial', description: 'Raises levy size and garrison strength.' },
  spymaster: { name: 'Spymaster', attribute: 'intrigue', description: 'Guards against plots and unravels enemy schemes.' },
  physician: { name: 'Court Physician', attribute: 'learning', description: 'Improves health of the ruling family.' },
  treasurer: { name: 'Treasurer', attribute: 'stewardship', description: 'Raises city income.' },
  captain: { name: 'Captain of the Watch', attribute: 'martial', description: 'Raises city garrison.' },
} as const;

/** Monthly salary a liege pays per council seat, by rank of the council's title. */
export const COUNCIL_SALARY: Record<TitleRank, number> = {
  kingdom: 8, duchy: 5, county: 3, barony: 2, city: 2,
};

/** Maximum characters in a letter (enforced by both the sim and the server's command schema). */
export const MAX_LETTER_LENGTH = 1000;

// ------------------------------------------------------------------ schemes (Phase 3)

export interface SchemeTypeDef {
  name: string;
  /** Gold to start. */
  cost: number;
  /** Base monthly progress. */
  progressPerMonth: number;
  /** Extra progress per point of the leader's intrigue. */
  progressPerIntrigue: number;
  /** Execution threshold. */
  progressNeeded: number;
  /** Monthly discovery chance floor. */
  discoveryBase: number;
  /** Discovery chance per point of the TARGET house's Spymaster intrigue. */
  discoveryPerSpymaster: number;
  /** Discovery chance REDUCED per point of your own Spymaster intrigue. */
  guardPerSpymaster: number;
  /** Schemes older than this (months) fizzle. */
  maxMonths: number;
  successBase: number;
  /** Execution success per point of leader intrigue. */
  successPerIntrigue: number;
  /** Execution success REDUCED per point of target Spymaster intrigue. */
  successPerGuard: number;
}

export const SCHEMES: Record<SchemeType, SchemeTypeDef> = {
  murder: {
    name: 'Murder', cost: 25,
    progressPerMonth: 6, progressPerIntrigue: 0.5, progressNeeded: 100,
    discoveryBase: 0.06, discoveryPerSpymaster: 0.012, guardPerSpymaster: 0.006,
    maxMonths: 48,
    successBase: 0.5, successPerIntrigue: 0.025, successPerGuard: 0.025,
  },
  abduction: {
    name: 'Abduction', cost: 60,
    progressPerMonth: 6, progressPerIntrigue: 0.5, progressNeeded: 100,
    discoveryBase: 0.10, discoveryPerSpymaster: 0.012, guardPerSpymaster: 0.006,
    maxMonths: 36,
    successBase: 0.35, successPerIntrigue: 0.025, successPerGuard: 0.02,
  },
  fabricate_claim: {
    name: 'Fabricated Claim', cost: 40,
    progressPerMonth: 5, progressPerIntrigue: 0.5, progressNeeded: 100,
    discoveryBase: 0.08, discoveryPerSpymaster: 0.012, guardPerSpymaster: 0.006,
    maxMonths: 48,
    successBase: 0.45, successPerIntrigue: 0.025, successPerGuard: 0.025,
  },
  spread_rumours: {
    name: 'Spread Rumours', cost: 15,
    progressPerMonth: 8, progressPerIntrigue: 0.5, progressNeeded: 100,
    discoveryBase: 0.02, discoveryPerSpymaster: 0.006, guardPerSpymaster: 0.006,
    maxMonths: 18,
    successBase: 0.55, successPerIntrigue: 0.02, successPerGuard: 0.01,
  },
  steal_funds: {
    name: 'Steal Funds', cost: 30,
    progressPerMonth: 6, progressPerIntrigue: 0.5, progressNeeded: 100,
    discoveryBase: 0.14, discoveryPerSpymaster: 0.015, guardPerSpymaster: 0.005,
    maxMonths: 36,
    successBase: 0.5, successPerIntrigue: 0.025, successPerGuard: 0.02,
  },
  befriend: {
    name: 'Befriend', cost: 20,
    progressPerMonth: 4, progressPerIntrigue: 0.5, progressNeeded: 100,
    discoveryBase: 0.015, discoveryPerSpymaster: 0.006, guardPerSpymaster: 0.006,
    maxMonths: 60,
    successBase: 0.5, successPerIntrigue: 0.02, successPerGuard: 0.01,
  },
};
export const SCHEME_MAX_PER_HOUSE = 1;
export const SCHEME_DISCOVERY_MIN = 0.01;
export const SCHEME_DISCOVERY_MAX = 0.4;
export const SCHEME_DISCOVERY_PROGRESS_FACTOR = 0.5;
export const SCHEME_SUCCESS_MIN = 0.05;
export const SCHEME_SUCCESS_MAX = 0.95;
export const SCHEME_DISCOVERY_OPINION = -30;
export const SCHEME_DISCOVERY_OPINION_DAYS = 2 * DAYS_PER_YEAR;
export const SCHEME_EXPOSED_OPINION = -50;
export const SCHEME_EXPOSED_OPINION_DAYS = 3 * DAYS_PER_YEAR;
export const SCHEME_EXPOSED_PRESTIGE = -25;
export const SCHEME_AI_CHANCE = 0.002;
export const SCHEME_AI_OPINION = -30;
/** AI befriends its liege when the liege's opinion of it is below this (deterministic gate). */
export const SCHEME_AI_BEFRIEND_OPINION = 20;
/** AI robs the richest reachable house only once its treasury passes this. */
export const SCHEME_AI_STEAL_MIN_GOLD = 400;
/** Spread rumours: slander opinion penalty applied realm-wide on success. */
export const SCHEME_RUMOUR_OPINION = -25;
export const SCHEME_RUMOUR_OPINION_DAYS = 3 * DAYS_PER_YEAR;
/** Steal funds: fraction of the target treasury taken, its absolute cap, and the floor below which a house is too poor to target. */
export const SCHEME_STEAL_FRACTION = 0.25;
export const SCHEME_STEAL_CAP = 200;
export const SCHEME_STEAL_MIN_GOLD = 50;
/** Befriend: sway opinion bonus on the target (and a smaller one on their house head). */
export const SCHEME_BEFRIEND_OPINION = 30;
export const SCHEME_BEFRIEND_HEAD_OPINION = 15;
export const SCHEME_BEFRIEND_OPINION_DAYS = 5 * DAYS_PER_YEAR;
/** Befriend exposure: only the embarrassed target takes a small penalty. */
export const SCHEME_BEFRIEND_EXPOSED_OPINION = -15;

// ------------------------------------------------------------------ vassal factions (Round B1)

/** A discontent vassal head joins (or helps found) a faction when its opinion of the liege falls to this or below. */
export const FACTION_JOIN_OPINION = -20;
/** The head leaves the faction once its opinion of the liege recovers to this or above (hysteresis band in between). */
export const FACTION_LEAVE_OPINION = -5;
/** Discontent direct vassals needed to found a faction against a liege. */
export const FACTION_MIN_MEMBERS = 2;
/** Combined member realm size / liege realm size at which the faction is ready to send its demand (B2). */
export const FACTION_WAR_STRENGTH_RATIO = 0.8;
/** A lower_taxes demand enforces this vassal tax rate (percent; stored as a fraction: /100). */
export const FACTION_DEMAND_TAX = 10;
/** An AI liege capitulates to a faction only while faction strength ≥ ratio × liege (no rng).
 * Members lie inside the liege's realm, so the ratio is bounded <1: demands fire at ready ≥0.8
 * (FACTION_WAR_STRENGTH_RATIO), lieges capitulate at ≥0.9, the 0.8–0.9 band refuses → faction war. */
export const FACTION_DEMAND_ACCEPT_RATIO = 0.9;

/** Chronicle kinds that concern private diplomacy: only shown to the houses named in the entry. */
const PRIVATE_CHRONICLE_KINDS = new Set([
  'proposal', 'letter', 'scheme_started', 'scheme_discovered', 'scheme_resolved', 'hook_gained',
]);
export const isPrivateChronicleKind = (kind: string): boolean => PRIVATE_CHRONICLE_KINDS.has(kind);

// ------------------------------------------------------------------ clock & seasons (Phase 5)

/** In-game hour (0..23) at which the yearly tick, autumn harvest and winter attrition run. */
export const HOUR_OF_YEARLY_TICK = 6;
/** Parity with the old min-1-travel-day rule: a march edge takes at least a full day. */
export const MIN_MARCH_EDGE_HOURS = 24;
/** Fraction of each field army's troops lost per winter month (December, January, February). */
export const WINTER_ATTRITION_MONTHLY = 0.02;
export const WINTER_MONTHS = [12, 1, 2];
/** Autumn harvest month (September). */
export const HARVEST_MONTH = 9;
/**
 * Food added per farmland level at harvest. A max-level (3) farmland produces 3 food/month,
 * so 6 per level x 3 levels = 18 food ≈ 2 months of that holding's farmland output.
 */
export const HARVEST_YIELD_PER_FARM_LEVEL = 6;

// ------------------------------------------------------------------ merchant path (Phase 5)

/** Resources traded on city markets; gold and manpower are never tradable. */
export const TRADABLE_RESOURCES = ['food', 'wool', 'timber', 'stone', 'iron', 'horses'] as const;
export type TradableResource = (typeof TRADABLE_RESOURCES)[number];

/** Reference price (integer gold per unit) of each tradable resource. */
export const BASE_PRICES: Record<TradableResource, number> = { food: 2, wool: 3, timber: 3, stone: 4, iron: 6, horses: 8 };

/** Market maker bid = max(1, BASE - 1), ask = BASE + 1 (integer gold). */
export const MAKER_TICK = 1;
/** Monthly maker rebalance floor for the market maker's gold: MAKER_GOLD_BASE + dev x 10. */
export const MAKER_GOLD_BASE = 100;
/** Monthly maker rebalance floor per resource: MAKER_STOCK_BASE + dev. */
export const MAKER_STOCK_BASE = 10;
/** Market orders expire after this many days (escrow returned). */
export const MARKET_ORDER_EXPIRY_DAYS = 90;
/** Max quantity of one market order. */
export const MAX_MARKET_ORDER_QTY = 100;

/**
 * Caravans (goods travel time): a BUY fill does not credit the buyer's stock at once — it
 * creates a delivery (caravan) that travels from the market city to the buyer house's seat.
 * deliveryDays = max(1, ceil(roadDays * CARAVAN_DAYS_PER_ROAD_DAY)); a house without a
 * reachable seat (landless) gets its goods after CARAVAN_UNREACHABLE_DAYS instead.
 * Sell orders keep escrow-at-placement (the goods were shipped to the market ahead of the sale).
 */
export const CARAVAN_DAYS_PER_ROAD_DAY = 1;
export const CARAVAN_UNREACHABLE_DAYS = 30;
/** Hard upper bound of any market tariff (the schema bound); cities are further limited by their tier cap. */
export const MAX_TARIFF_RATE = 0.15;

// ------------------------------------------------------------------ cities (Design pass 1, step 3)

export type CityTierKey = 'market_town' | 'town' | 'city' | 'great_city';
export interface CityTierDef {
  key: CityTierKey;
  name: string;
  minDevelopment: number;
  maxDevelopment: number;
  /** Company licences (companies) the city allows. */
  licences: number;
  tariffCap: number;
  /** Seats on the mayor's council shown for the tier. */
  councilSeats: number;
}
export const CITY_TIERS: CityTierDef[] = [
  { key: 'market_town', name: 'Market town', minDevelopment: 1, maxDevelopment: 3, licences: 2, tariffCap: 0.05, councilSeats: 2 },
  { key: 'town', name: 'Town', minDevelopment: 4, maxDevelopment: 6, licences: 4, tariffCap: 0.08, councilSeats: 2 },
  { key: 'city', name: 'City', minDevelopment: 7, maxDevelopment: 9, licences: 7, tariffCap: 0.10, councilSeats: 2 },
  { key: 'great_city', name: 'Great city', minDevelopment: 10, maxDevelopment: 10, licences: 10, tariffCap: 0.12, councilSeats: 2 },
];
export function cityTierOf(development: number): CityTierDef {
  const dev = Math.floor(development);
  return CITY_TIERS.find((t) => dev >= t.minDevelopment && dev <= t.maxDevelopment) ?? (dev > 10 ? CITY_TIERS[3] : CITY_TIERS[0]);
}
/** Share of every treasury income the mayor's house receives as salary. */
export const CITY_SALARY_SHARE = 0.1;
/** Minimum development with a guild master vote of weight 2 for the Lord Mayor (Town and larger). */
export const GUILD_VOTE_MIN_DEVELOPMENT = 4;
export const GUILD_VOTE_WEIGHT = 2;
/** Every this many gold of trade adds 1 commerce. */
export const COMMERCE_GOLD_PER_POINT = 100;

export interface DistrictDef {
  key: DistrictKey;
  name: string;
  description: string;
  /** Treasury gold. */
  cost: number;
  /** Build time in game months. */
  months: number;
}
export const DISTRICT_DEFS: Record<DistrictKey, DistrictDef> = {
  market_quarter: { key: 'market_quarter', name: 'Market quarter', description: '+25 % market liquidity, -1 % fees for all traders.', cost: 150, months: 12 },
  craft_quarter: { key: 'craft_quarter', name: 'Craft quarter', description: '+15 % output of the companies in the city.', cost: 180, months: 12 },
  harbour: { key: 'harbour', name: 'Harbour', description: 'Coast only. Caravans from the city travel 30 % faster, +1 prosperity a month.', cost: 250, months: 18 },
  guildhall: { key: 'guildhall', name: 'Guildhall', description: '+25 % guild xp earned in the city.', cost: 250, months: 18 },
  watch_house: { key: 'watch_house', name: 'Watch house', description: '+50 % city garrison, -3 % success of schemes against people seated here.', cost: 200, months: 12 },
  cathedral: { key: 'cathedral', name: 'Cathedral', description: '+5 prosperity a month, +2 prestige a year for the Lord Mayor.', cost: 300, months: 24 },
};
export const DISTRICT_KEYS = Object.keys(DISTRICT_DEFS) as DistrictKey[];
/** Districts a city may hold: floor(development / 2) from development 3. */
export function districtSlots(development: number): number {
  const dev = Math.floor(development);
  return dev < 3 ? 0 : Math.floor(dev / 2);
}
export const MARKET_QUARTER_LIQUIDITY = 1.25;
export const MARKET_QUARTER_FEE_CUT = 0.01;
export const CRAFT_QUARTER_OUTPUT = 1.15;
export const HARBOUR_CARAVAN_SPEEDUP = 0.7;
export const GUILDHALL_XP = 1.25;
export const WATCH_HOUSE_GARRISON = 1.5;
export const WATCH_HOUSE_SCHEME_MALUS = 0.03;
export const CATHEDRAL_PRESTIGE_PER_YEAR = 2;

/** Guild xp thresholds: derived ranks (guildmaster > master > journeyman). */
export const GUILD_XP_JOURNEYMAN = 60;
export const GUILD_XP_MASTER = 180;
export const GUILD_XP_GUILDMASTER = 420;

/** Civic Lord Mayor term in days (5 game years); elections fire on the first monthly tick at/after the day. */
export const CITY_TERM_DAYS = 1800;
/** Worldgen spread for the first election so all cities do not vote on the same day. */
export const CITY_ELECTION_STAGGER_DAYS = 180;

/** Company licence cost by level (index 0 unused); paid to the city holder house. */
export const COMPANY_LICENCE_COSTS = [0, 60, 120, 240];
/** Company input & output scale by licence level (index 0 unused). */
export const COMPANY_LICENCE_THROUGHPUT = [0, 1, 1.5, 2];
/** Company wages per month by licence level (index 0 unused). */
export const COMPANY_WAGES = [0, 4, 6, 8];

/** Production chains: input resource/qty -> output resource/qty at licence level 1. */
export const COMPANY_CHAINS = {
  smelter: { inputResource: 'timber', inputQty: 20, outputResource: 'iron', outputQty: 6 },
  quarry: { inputResource: 'timber', inputQty: 15, outputResource: 'stone', outputQty: 10 },
  stud: { inputResource: 'food', inputQty: 20, outputResource: 'horses', outputQty: 4 },
  weaver: { inputResource: 'wool', inputQty: 15, outputResource: 'gold', outputQty: 8 },
} as const;
export type CompanyChain = keyof typeof COMPANY_CHAINS;

/** Loans between houses. */
export const LOAN_MIN_AMOUNT = 10;
export const LOAN_MAX_AMOUNT = 500;
export const LOAN_MIN_RATE = 0.05;
export const LOAN_MAX_RATE = 0.5;
/** Outstanding debt is capped at this multiple of the principal. */
export const LOAN_OUTSTANDING_CAP = 3;
/** AI lieges accept a loan only while their gold is below this need (by primary-title rank). */
export const LOAN_NEED_BY_RANK: Record<TitleRank, number> = { kingdom: 300, duchy: 200, county: 120, barony: 60, city: 60 };
/** AI borrowers repay at least this fraction of the principal per month when flush. */
export const LOAN_MONTHLY_REPAY = 0.1;
/** Consecutive months with gold <= 0 before a borrower defaults: opinion -25 + one chronicle. */
export const LOAN_DEFAULT_MISSED = 3;

// ------------------------------------------------------------------ skills that grow by doing (Design pass 1, step 1)
/** Skill = aptitude (0..10) + training rank (0..10) + modifiers. Rank r needs SKILL_RANK_XP x r^2 total XP. */
export const SKILL_RANK_XP = 50;
export const SKILL_MAX_RANK = 10;
export const SKILL_MAX_APTITUDE = 10;
/** Extra XP fraction for the focus skill; suspended for SKILL_FOCUS_CHANGE_DAYS after a change. */
export const SKILL_FOCUS_BONUS = 0.5;
export const SKILL_FOCUS_CHANGE_DAYS = DAYS_PER_YEAR;
/** Learning-rate modifiers (additive with the focus bonus): genius, and per Learning point. */
export const SKILL_GENIUS_BONUS = 0.25;
export const SKILL_LEARNING_BONUS_PER_POINT = 0.01;
/** Age multipliers on XP: older than the first age x0.75, older than the second x0.5. */
export const SKILL_SLOW_LEARNER_AGE = 50;
export const SKILL_SLOW_LEARNER_FACTOR = 0.75;
export const SKILL_OLD_LEARNER_AGE = 65;
export const SKILL_OLD_LEARNER_FACTOR = 0.5;
/** Computed aging modifier (never stored): from this age Prowess loses 1 per year and Martial 1 per 3 years. */
export const SKILL_AGING_START_AGE = 50;
export const SKILL_AGING_MARTIAL_YEARS = 3;
/** Monthly XP sources. */
export const XP_COUNCIL_SEAT = 10;
export const XP_HOUSE_HEAD_STEWARDSHIP = 6;
export const XP_COMPANY_MANAGER = 8;
export const XP_BATTLE_COMMAND_WON = 40;
export const XP_BATTLE_COMMAND_LOST = 20;
export const XP_BATTLE_KNIGHT_PROWESS = 25;
export const XP_SCHEME_MONTHLY = 15;
export const XP_SCHEME_SUCCESS = 60;
export const XP_DIPLOMACY_ACCEPTED = 10;
export const XP_TUTOR_LEARNING = 5;
export const XP_TUTORED_CHILD = 8;
export const XP_KNIGHT_SERVICE = 5;
/** Children 6..15: small general XP in every skill each month (upbringing), on top of the tutored skill. */
export const XP_CHILDHOOD_GENERAL = 10;
/** End-of-education XP in the focus skill by tutor quality (tutor skill >= 15 / >= 10 / else). */
export const XP_EDUCATION_END = { high: 1200, mid: 800, low: 400 } as const;
